import { ResolveMacroChipDisplay } from "../macro-events"

const DEFAULT_STYLES = {
    background: "rgba(var(--mui-palette-info-mainChannel) / 0.12)",
    borderColor: "rgba(var(--mui-palette-info-mainChannel) / 0.3)",
    color: "var(--mui-palette-info-dark, #1165B3)",
}

/**
 * Fires the ResolveMacroChipDisplay event to determine how a token
 * should be rendered as a visual chip. Providers register handlers
 * by prefix sub-type (e.g. ResolveMacroChipDisplay("cmp").handleOnce(...))
 * to customise label, colour, icon, and tooltip.
 */
export function resolveChipDisplay(tokenId, macro) {
    const result = {
        label: macro?.name ?? tokenId,
        ...DEFAULT_STYLES,
        tooltip: "",
        icon: null,
    }
    ResolveMacroChipDisplay.call({ tokenId, macro, result })
    const prefix = getTokenPrefix(tokenId)
    if (prefix) {
        ResolveMacroChipDisplay(prefix).call({ tokenId, macro, result })
    }
    return result
}

/**
 * Builds an inline HTML string for a chip span.
 * The span carries data-macro-token so stripChipHtml can recover the tokenId.
 */
export function buildChipHtml(tokenId, chipDisplay) {
    const label = escapeAttr(truncateLabel(chipDisplay.label ?? tokenId))
    const tooltip = escapeAttr(chipDisplay.tooltip || chipDisplay.label || tokenId)
    const prefix = getTokenPrefix(tokenId) ?? ""
    const style = [
        `background:${sanitizeStyleValue(chipDisplay.background)}`,
        `border:1px solid ${sanitizeStyleValue(chipDisplay.borderColor)}`,
        `color:${sanitizeStyleValue(chipDisplay.color)}`,
        "border-radius:12px",
        "padding:0 8px",
        "font-size:0.8rem",
        "line-height:1.6",
        "display:inline-block",
        "cursor:default",
        "user-select:all",
        "white-space:nowrap",
    ].join(";")
    const prefixAttr = prefix ? ` data-macro-prefix="${escapeAttr(prefix)}"` : ""
    return `<span class="macro-chip" data-macro-token="${escapeAttr(tokenId)}" data-macro-label="${label}"${prefixAttr} contenteditable="false" style="${style}" title="${tooltip}">${label}</span>`
}

/**
 * Convenience: resolve display via IoC event then build chip HTML.
 */
export function buildChipHtmlForToken(tokenId, macroMap, macroOverride) {
    const macro = macroOverride ?? macroMap?.[tokenId]
    const chipDisplay = resolveChipDisplay(tokenId, macro)
    return buildChipHtml(tokenId, chipDisplay)
}

/**
 * Converts chip HTML spans back to $tokenId$ delimiters.
 * Used when saving or normalising schedule content.
 */
export function stripChipHtml(textOrHtml) {
    if (!textOrHtml || textOrHtml === "undefined") return ""
    return `${textOrHtml}`.replace(
        /<span[^>]*?\bdata-macro-token="([^"]*)"[^>]*?>[\s\S]*?<\/span>/g,
        (match, tokenId) => `$${tokenId}$`
    )
}

export function getTokenPrefix(tokenId) {
    if (!tokenId) return null
    const colonIndex = tokenId.indexOf(":")
    if (colonIndex <= 0) return null
    return tokenId.slice(0, colonIndex)
}

function escapeAttr(str) {
    return `${str ?? ""}`.replace(/&/g, "&amp;").replace(/"/g, "&quot;")
}

function truncateLabel(label, maxLength = 60) {
    if (!label || label.length <= maxLength) return label
    return `\u2026${label.slice(-(maxLength - 1))}`
}

function sanitizeStyleValue(value) {
    if (!value) return "inherit"
    return `${value}`.replace(/[;"'<>{}]/g, "")
}
